import { useEffect } from "react";

import { useKpiDatasetKind } from "./useKpiDatasetKind";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchKpiScoresLatest,
  kpiScoresLatestKey,
  type XFactorKpiScoresLatest,
  type XFactorKpiScoresLatestCategory,
} from "../Store/Reducers/XFactorKpiSlice";

export type { XFactorKpiScoresLatest, XFactorKpiScoresLatestCategory };

export type XFactorKpiScoresLatestState = {
  data: XFactorKpiScoresLatest | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
};

/**
 * Newest **scored** snapshot for one account (GET kpi/scores/latest, live
 * 2026-09-21) — no AI briefing required, unlike useXFactorKpiAccountBriefing.
 * Fast enough for an auto-loading surface with no date picker, e.g. the
 * Account Health strip on Account Synopsis. `briefing_available` says
 * whether the drill-down (Situation Card / bubble click) will also succeed
 * for the resolved date. Data lives in XFactorKpiSlice.
 */
export const useXFactorKpiScoresLatest = (
  accountId?: string | null
): XFactorKpiScoresLatestState => {
  const dispatch = useAppDispatch();
  const datasetKind = useKpiDatasetKind();
  const key = accountId ? kpiScoresLatestKey({ accountId, datasetKind }) : null;
  const entry = useAppSelector(state =>
    key ? state.xfactorKpi.scoresLatest[key] : undefined
  );

  useEffect(() => {
    if (!accountId) return;
    dispatch(fetchKpiScoresLatest({ accountId, datasetKind }));
  }, [dispatch, accountId, datasetKind]);

  return {
    data: entry?.data ?? null,
    loading: entry ? entry.loading : Boolean(accountId),
    error: entry?.error ?? null,
    notFound: entry?.notFound ?? false,
  };
};

export default useXFactorKpiScoresLatest;
